import React, { useState, useCallback } from "react";
import { 
  SuccessMessage,
  FailMessage,
  Loading,
  ReportsJournalSelect,
  FormatValueToDecimal,
  CompanySelect,
  FormatDateToMDY
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from 'axios';
import { PDFDocument, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export default function JournalVoucher() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
  
  const getCurrency = JSON.parse(localStorage.getItem("currency") || "{}");
//#endregion






//#region Journal 
  const GJ_DOCUMENT_TYPE = 3;
  const [journal, setJournal] = useState(null);
  const [company, setCompany] = useState(null);
  const [showJournalSelect, setShowJournalSelect] = useState(false);

  const openJournalSelect = () => {
    setShowJournalSelect(true)
  }
  const closeJournalSelect = () => {
    setShowJournalSelect(false)
  }
//#endregion






//#region Generate
  const fetchReportData = useCallback(async (_data) => {
    try {
      const response = await axios.get(`${apiUrl}/api/Report/report-data/documentId/${_data?.id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, []);

  const generateHandleClick = async () => {
    if (!journal || !company) {
      setFailMessage("Please fill up required fields")
      return;
    }

    setLoading(true)

    const _data = await fetchReportData(journal);

    try {
      // Load the existing PDF template
      const pdfUrl = `${window.location.origin}/docs/JOURNAL_VOUCHER.pdf`;
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      let page = pdfDoc.getPages()[0];

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      // Helper function to wrap text
      const wrapText = (text, maxWidth, font, fontSize) => {
        if (!text) return [""];
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';

        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const width = font.widthOfTextAtSize(testLine, fontSize);
          if (width <= maxWidth) {
            currentLine = testLine;
          } else {
            lines.push(currentLine);
            currentLine = word;
          }
        }
        lines.push(currentLine);
        return lines;
      };

      // Header Info
      page.drawText(company.name?.toString() ?? "", {
        x: 25, y: 741, size: 16, font: calibriBold, color: rgb(0, 0, 0),
      });

      page.drawText(company.address?.toString() ?? "", {
        x: 25, y: 731, size: 9, font: calibriFont, color: rgb(0, 0, 0),
      });

      page.drawText(company.tin?.toString() ?? "", {
        x: 25, y: 721, size: 9, font: calibriFont, color: rgb(0, 0, 0),
      });

      page.drawText(_data.documentNumber?.toString() ?? "", {
        x: 80, y: 612, size: 9, font: calibriFont, color: rgb(0, 0, 0),
      });

      page.drawText(FormatDateToMDY(_data.documentDate), {
        x: 430, y: 612, size: 9, font: calibriFont, color: rgb(0, 0, 0),
      });

      page.drawText(_data.referenceNumber?.toString() ?? "", {
        x: 440, y: 589, size: 9, font: calibriFont, color: rgb(0, 0, 0),
      });

      // TABLE LOGIC
      let yPosition = 520;
      const colWidthAccount = 130; 
      const colWidthHeader = 120;  
      const lineHeight = 10;       

      for (let index = 0; index < _data?.journalLines?.length; index++) {
        const itemData = _data?.journalLines[index];

        const accountString = `${itemData.glAccountNumber?.toString()} - ${itemData.glAccountName?.toString() ?? ""}`;
        const headerString = `${itemData.itemText?.toString() ?? ""}`;

        // Wrap the text for both columns
        const accountLines = wrapText(accountString, colWidthAccount, calibriFont, 9);
        const headerLines = wrapText(headerString, colWidthHeader, calibriFont, 9);

        // Find how many lines this row will take
        const maxLines = Math.max(accountLines.length, headerLines.length);

        // FIX: Use standard for-loop instead of .forEach to avoid "no-loop-func" error
        for (let i = 0; i < accountLines.length; i++) {
          page.drawText(accountLines[i], {
            x: 45,
            y: yPosition - (i * lineHeight),
            size: 9,
            font: calibriFont,
            color: rgb(0, 0, 0),
          });
        }

        // FIX: Use standard for-loop instead of .forEach to avoid "no-loop-func" error
        for (let j = 0; j < headerLines.length; j++) {
          page.drawText(headerLines[j], {
            x: 180,
            y: yPosition - (j * lineHeight),
            size: 9,
            font: calibriFont,
            color: rgb(0, 0, 0),
          });
        }

        // Draw Amount (Keep it aligned with the top of the row)
        const amountStr = FormatValueToDecimal(itemData.amount)?.toString() ?? "";
        if (itemData.debitCredit === "Debit") {
          page.drawText(amountStr, { x: 315, y: yPosition, size: 9, font: calibriFont, color: rgb(0, 0, 0) });
        } else {
          page.drawText(amountStr, { x: 450, y: yPosition, size: 9, font: calibriFont, color: rgb(0, 0, 0) });
        }

        // Adjust yPosition for the next entry
        const dynamicIncrement = Math.max(20, (maxLines * lineHeight) + 5);
        yPosition -= dynamicIncrement;
      }

      // Footer Amounts
      page.drawText(FormatValueToDecimal(_data.totalDebitAmount)?.toString() ?? "", {
        x: 315, y: 160, size: 9, font: calibriFont, color: rgb(0, 0, 0),
      });

      page.drawText(FormatValueToDecimal(_data.totalCreditAmount)?.toString() ?? "", {
        x: 450, y: 160, size: 9, font: calibriFont, color: rgb(0, 0, 0),
      });

      // Save and Download PDF
      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Journal_Voucher.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }

    setLoading(false)
  }
//#endregion






//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasJournalVoucher) {
    return <NoAccess />;
  }
//#endregion





//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon fontSize="large" className="text-theme-color" />
              {`Journal Voucher`}
            </h2>
            <p className="text-gray-500 mt-1">Manual entry document.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Transaction <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-row items-center">
              <input
                type="text"
                placeholder="Select Transaction"
                className="w-full border border-gray-300 text-sm rounded-l-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color transition"
                value={`${journal ? `${journal?.documentHeaderText} - ${journal?.company?.companyName1} - ${getCurrency?.symbol ? getCurrency?.symbol : ""}${FormatValueToDecimal(journal?.totalDebitAmount)}` : ""}`}
                readOnly
              />
              {!journal ?
                <button className="bg-theme-color hover:bg-theme-color-h p-[5px] rounded-r-lg" onClick={openJournalSelect}>
                  <ManageSearchIcon className="text-white"/>
                </button> 
                :
                <button className="bg-red-400 hover:bg-red-500 p-[5px] rounded-r-lg" onClick={()=>setJournal(null)}>
                  <RemoveCircleIcon className="text-white"/>
                </button>
              }
            </div>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Company <span className="text-red-500">*</span>
            </label>
            <CompanySelect data={company} setData={setCompany} isEditable={true}/>
          </div>
        </div>

        <button onClick={generateHandleClick} className="mt-10 flex items-center gap-2 bg-theme-color text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-theme-color-h transition shadow-md">
          <RestartAltIcon fontSize="small" /> Generate
        </button>
      </div>

      <ReportsJournalSelect 
        visible={showJournalSelect} 
        onClose={closeJournalSelect}
        modalName={"Select Transaction"}
        documentTypeId={GJ_DOCUMENT_TYPE}
        data={journal} setData={setJournal}
      />

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}